/**
 * Coalesce identical in-flight GETs into one network request.
 *
 * Several shell components mount together and ask for the same data on every
 * page load (Sidebar + TopBar + GlobalSearch all fetch the user menu; Sidebar +
 * TopBar + TasksWidget the marking queue). On prod the backend is a single
 * process, so every duplicate queues behind the real work.
 *
 * Only shares while the request is PENDING — once it settles the next call goes
 * to the network again, so a refetch after a change (e.g. after grading) is
 * never served stale. Each caller gets its own deep copy, so one component
 * mutating its result can't affect another.
 */
const inflight = new Map<string, Promise<unknown>>();

export function sharedRequest<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  let pending = inflight.get(key) as Promise<T> | undefined;
  if (!pending) {
    pending = fetcher().finally(() => inflight.delete(key));
    inflight.set(key, pending);
  }
  return pending.then((value) => structuredClone(value));
}
